import { useEffect } from 'react';

/**
 * Hook pour détecter l'inactivité et nettoyer automatiquement le localStorage
 * @param {number} timeout - Temps d'inactivité en millisecondes (par défaut: 15 minutes)
 * @param {boolean} enabled - Activer ou désactiver le détecteur (par défaut: true)
 */
export const useInactivityDetector = (timeout = 15 * 60 * 1000, enabled = true) => {
  useEffect(() => {
    if (!enabled) return;

    let timeoutId;
    let events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    // Fonction pour réinitialiser le timer d'inactivité
    const resetTimer = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      // Démarrer un nouveau timer
      timeoutId = setTimeout(() => {
        console.log('Inactivité détectée - Nettoyage du localStorage');
        clearAuthData();
        // Ne recharger la page que si on n'est pas sur une page d'authentification
        if (!window.location.pathname.includes('/login') && 
            !window.location.pathname.includes('/page-register') && 
            !window.location.pathname.includes('/page-forgot-password')) {
          window.location.reload();
        }
      }, timeout);
    };

    // Fonction pour nettoyer les données d'authentification
    const clearAuthData = () => {
      // Supprimer toutes les clés liées à l'authentification
      const keysToRemove = [
        'auth_token',
        'userDetails',
        'user',
        'token',
        'selectedPlan',
        'persist:root',
        'persist:auth', // Pour Redux persist
        // Ajouter d'autres clés si nécessaire
      ];
      
      keysToRemove.forEach(key => {
        localStorage.removeItem(key);
      });
      
      // Nettoyer aussi le sessionStorage pour être sûr
      sessionStorage.clear();
    };

    // Démarrer le timer initial
    resetTimer();

    // Ajouter les écouteurs d'événements
    events.forEach(event => {
      window.addEventListener(event, resetTimer, true);
    });

    // Écouter les changements de visibilité de la page
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Quand l'utilisateur revient sur la page, vérifier si le token est encore valide
        const userDetails = localStorage.getItem('userDetails');
        const authToken = localStorage.getItem('auth_token');
        
        if (userDetails) {
          try {
            const parsed = JSON.parse(userDetails);
            if (parsed.expireDate) {
              const expireDate = new Date(parsed.expireDate);
              const now = new Date();
              if (now > expireDate) {
                console.log('Token expiré - Nettoyage du localStorage');
                clearAuthData();
                // Ne recharger la page que si on n'est pas sur une page d'authentification
                if (!window.location.pathname.includes('/login') && 
                    !window.location.pathname.includes('/page-register') && 
                    !window.location.pathname.includes('/page-forgot-password')) {
                  window.location.reload();
                }
                return;
              }
            }
          } catch (error) {
            console.error('Erreur lors de la vérification du token:', error);
            clearAuthData();
            // Ne recharger la page que si on n'est pas sur une page d'authentification
            if (!window.location.pathname.includes('/login') && 
                !window.location.pathname.includes('/page-register') && 
                !window.location.pathname.includes('/page-forgot-password')) {
              window.location.reload();
            }
            return;
          }
        }
      }
      resetTimer();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Écouter les changements d'onglet
    window.addEventListener('focus', resetTimer);
    window.addEventListener('blur', resetTimer);

    // Nettoyer les écouteurs d'événements
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      events.forEach(event => {
        window.removeEventListener(event, resetTimer, true);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', resetTimer);
      window.removeEventListener('blur', resetTimer);
    };
  }, [timeout, enabled]);
};
